import { CATEGORIES, CATEGORY_LABEL } from "../../src/lib/catalog-copy.ts";
import type { Category } from "../../src/lib/catalog-copy.ts";
import { money } from "../../src/lib/money.ts";

// The style picker's data, shared by the designer and the Studio (no DOM here, so the tests can run it).

export interface Dual {
  bankCents: number;
  cardCents: number;
}

// One style as the catalog answer lists it (src/api/catalog.ts). `from` is the designer's only.
export interface StyleSummary {
  key: string;
  name: string;
  description: string;
  category: Category;
  colorCount: number;
  sizes: string[];
  swatch: { color: string; thumbUrl: string } | null;
  from?: Dual;
}

export interface CategoryGroup {
  key: Category;
  label: string;
  styles: StyleSummary[];
}

// A style with more colors than this gets the "Find a color" box.
export const FILTER_AT = 24;

// The category chips, in the fixed order, leaving out any with no style (no empty chip to tap).
export function groupCategories(styles: StyleSummary[]): CategoryGroup[] {
  return CATEGORIES.map((key) => ({ key, label: CATEGORY_LABEL[key], styles: styles.filter((s) => s.category === key) }))
    .filter((g) => g.styles.length > 0);
}

// The category to show: the one picked when it still has styles, else the picked style's, else the first.
export function shownCategory(groups: CategoryGroup[], picked: Category | null, style: string | null): Category | null {
  if (picked && groups.some((g) => g.key === picked)) return picked;
  return groups.find((g) => g.styles.some((s) => s.key === style))?.key ?? groups[0]?.key ?? null;
}

// Colors whose name holds every word typed, in any order and case ("navy heather" finds "Heather Navy").
export function matchesColor(color: string, query: string): boolean {
  const name = color.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((w) => name.includes(w));
}

export function filterColors<T extends { color: string }>(colors: T[], query: string): T[] {
  return colors.filter((c) => matchesColor(c.color, query));
}

export const colorCountText = (n: number) => `${n} ${n === 1 ? "color" : "colors"}`;

// The style card's price: the two prices of its lowest list price, never a percentage.
export const fromLine = (d: Dual) => `From ${money(d.bankCents)} bank / ${money(d.cardCents)} card`;

// What the filter leaves showing, for the line under the box.
export function filterCountText(shown: number, total: number, query: string): string {
  if (!query.trim()) return `${colorCountText(total)}.`;
  return shown === 0 ? `No color matches "${query.trim()}".` : `${shown} of ${colorCountText(total)}.`;
}

// Signed photo links last 600 s; the page fetches its answers again when they are older than this.
export const STALE_MS = 8 * 60_000;

export const isStale = (loadedAt: number, now: number) => now - loadedAt >= STALE_MS;

// Style answers kept on the page by key until they go stale, so flipping between styles does not ask the server again
// (the designer shares one rate limit with every other action).
export function styleCache<T>() {
  const kept = new Map<string, { value: T; at: number }>();
  return {
    // The answer and when it was fetched, or null when there is none or it is stale (and then it is dropped).
    get(key: string, now: number): { value: T; at: number } | null {
      const hit = kept.get(key);
      if (!hit) return null;
      if (!isStale(hit.at, now)) return hit;
      kept.delete(key);
      return null;
    },
    set(key: string, value: T, at: number): void {
      kept.set(key, { value, at });
    },
    clear(): void {
      kept.clear();
    },
  };
}

// Runs a refresh once at a time: a second call while one runs is dropped, and a call after it finished runs again.
export function onceAtATime(fn: () => Promise<void>): () => Promise<void> {
  let running = false;
  return async () => {
    if (running) return;
    running = true;
    try {
      await fn();
    } finally {
      running = false;
    }
  };
}
